import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react'
import { getAllPlayers } from '../../Api/players/players.api'
import { getAllTeams } from '../../Api/teams/teams.api'
import { fetchSeasonEvents } from '../../Api/events/events.api'
import { EventGQL } from '../../Api/events/events.types'
import { PlayerData } from '../../Api/players/players.types'
import { Team } from '../../Api/teams/teams.types'
import { Season, SeasonType, useSeasonContext } from '../SeasonContext/SeasonContext'
import {
  applyLeaderBoundaries,
  getLeaderTeam,
  PlayerSortKeys,
  sortPlayerFunc,
} from '../../Utils/PlayerHelpers/playerHelpers'
import { filterPLLTeams } from '../Controllers/Utils/filtering'
import {
  filterChampSeriesTeams,
  sortTeamsFunc,
} from '../../Utils/TeamHelpers/teamHelpers'
import { isLiveGame } from '../../Utils/EventHelpers/eventHelpers'
import { mapLiveEventForSeasonEventsUpdate, updateInProgressGame } from '../../Utils/EventHelpers/boxscoreHelpers'
import { fetchLeagueLeaders } from '../../Api/players/players.api'
import {
  csLeadersStatArray,
  seasonLeadersStatArray,
} from '../../Constants/PlayerConstants'
import { LeagueLeader } from '../../Api/players/players.types'
import { useStorageContext } from '../StorageContext'
import { STORAGE_NAMES } from '../../Constants'

type StatsContextType = {
  isPlayersLoading: boolean
  isTeamsLoading: boolean
  isEventsLoading: boolean
  isLeagueLeadersLoading: boolean
  players: PlayerData[]
  postPlayers: PlayerData[]
  csPlayers: PlayerData[]
  teams: Team[]
  events: EventGQL[]
  leagueLeaders: LeagueLeader[]
  yearObj: Season
  filterLeadersByStat: (stat: PlayerSortKeys) => LeagueLeader[]
  fetchLeaders: (season: Season, limit?: number, avgOn?: boolean) => Promise<void>
  getTopTeamsByStat: (stat: string, limit: number) => Team[]
  getTopPlayersByTeamAndStat: (
    teamId: string,
    stat: PlayerSortKeys,
    limit: number
  ) => PlayerData[]
}

export const StatsContext = createContext<StatsContextType | null>(null)

export const StatsProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { seasonSelect } = useSeasonContext()
  const { isStorageReady, fetchAndCache } = useStorageContext()
  const [yearObj, setYearObj] = useState<Season>(seasonSelect)
  const [isPlayersLoading, setIsPlayersLoading] = useState<boolean>(true)
  const [isTeamsLoading, setIsTeamsLoading] = useState<boolean>(true)
  const [isEventsLoading, setIsEventsLoading] = useState<boolean>(true)
  const [isLeagueLeadersLoading, setIsLeagueLeadersLoading] = useState<boolean>(true)
  const [players, setPlayers] = useState<PlayerData[]>([])
  const [postPlayers, setPostPlayers] = useState<PlayerData[]>([])
  const [csPlayers, setCSPlayers] = useState<PlayerData[]>([])
  const [leagueLeaders, setLeagueLeaders] = useState<LeagueLeader[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [events, setEvents] = useState<EventGQL[]>([])
  const gameCheckIntervalRef = useRef<any | null>(null)
  const liveGameIntervalRef = useRef<any | null>(null)

  useEffect(() => {
    if (isStorageReady) {
      Promise.all([fetchLeaders(yearObj), fetchData(yearObj)])
    }
    return () => {
      clearIntervals()
    }
  }, [isStorageReady])

  useEffect(() => {
    if (events.length > 0) runCheckForLiveGameInterval(events)
  }, [events])

  useEffect(() => {
    const yearChanged = seasonSelect.year !== yearObj.year
    const seasonTypeChanged = seasonSelect.seasonType !== yearObj.seasonType
    if (seasonTypeChanged && !yearChanged) {
      setIsLeagueLeadersLoading(true)
      Promise.all([fetchLeaders(seasonSelect), fetchPlayersBySeason(seasonSelect)])
      return
    }
    if (yearChanged) {
      // Refetch & update all stats
      setIsLeagueLeadersLoading(true)
      setPlayers([])
      setCSPlayers([])
      setPostPlayers([])
      Promise.all([fetchLeaders(seasonSelect), fetchData(seasonSelect)])
    }
  }, [seasonSelect])

  const fetchData = async (select: Season) => {
    await Promise.all([
      fetchAllPlayers(select),
      fetchAllTeams(select),
      fetchAllEvents(select),
    ])
    setYearObj(select)
    return
  }

  const fetchPlayersBySeason = async (season: Season) => {
    if (
      (season.seasonType === 'champseries' && csPlayers.length < 1) ||
      (season.seasonType === 'post' && postPlayers.length < 1) ||
      (season.seasonType === 'regular' && players.length < 1)
    ) {
      await fetchAllPlayers(season)
    }
    setYearObj(season)
  }

  // PLAYER FETCHING //
  const fetchAllPlayers = async (season: Season) => {
    setIsPlayersLoading(true)

    const fetcher = async (): Promise<PlayerData[]> => {
      try {
        const allPlrs = await getAllPlayers(season.year, season.seasonType)
        if (allPlrs.length > 0) {
          const allPWithTeam = extractPlayerTeam(allPlrs)
          return allPWithTeam
        }
        return []
      } catch (err) {
        console.error('fetchAllPlayers error', err)
        return []
      }
    }

    const onUpdate = (players: PlayerData[]) => {
      setPlayersBySeasonType(players, season.seasonType)
      setIsPlayersLoading(false)
    }

    try {
      await fetchAndCache<PlayerData[]>(
        `${STORAGE_NAMES.season_players}_${season.year}_${season.seasonType}`,
        fetcher,
        onUpdate
      )
    } catch (err) {
      console.error('fetchAllPlayers fetchAndCache error', err)
      setIsPlayersLoading(false)
    }
  }

  const setPlayersBySeasonType = (players: PlayerData[], seasonType: SeasonType) => {
    if (seasonType === 'champseries') {
      setCSPlayers(players)
    } else if (seasonType === 'post') {
      setPostPlayers(players)
    } else {
      setPlayers(players)
    }
  }

  const fetchLeaders = async (season: Season, limit: number = 5) => {
    const fetcher = async (): Promise<LeagueLeader[]> => {
      try {
        const statsList =
          season.seasonType === 'champseries'
            ? csLeadersStatArray
            : seasonLeadersStatArray
        const leaders = await fetchLeagueLeaders(
          season.year,
          season.seasonType,
          statsList,
          limit
        )
        return leaders || []
      } catch (err) {
        console.error('fetchLeaders error', err)
        return []
      }
    }

    const onUpdate = (leaders: LeagueLeader[]) => {
      setLeagueLeaders(leaders)
      setIsLeagueLeadersLoading(false)
    }

    try {
      await fetchAndCache<LeagueLeader[]>(
        `${STORAGE_NAMES.league_player_leaders}_${season.year}_${season.seasonType}`,
        fetcher,
        onUpdate
      )
    } catch (err) {
      console.error('fetchLeaders fetchAndCache error', err)
      setIsLeagueLeadersLoading(false)
    }
  }

  // TEAM FETCHING //
  const fetchAllTeams = async (season: Season, sortBy?: string) => {
    let teamRes: Team[] | null = []
    setIsTeamsLoading(true)
    try {
      teamRes = await getAllTeams(season.year, sortBy)
      if (teamRes.length > 0) {
        setTeams(teamRes)
      }
      setIsTeamsLoading(false)
    } catch (err) {
      console.log(err)
      setIsTeamsLoading(false)
    }
  }

  // EVENTS FETCHING //
  /**
   * Fetch all events for a season
   * Gets from cache if available, fetches new and passes to onUpdate
   * onUpdate sets events and loading
   * @param season - The season to fetch events for
   * @returns void
   */
  const fetchAllEvents = async (season: Season) => {
    const fetcher = async (): Promise<EventGQL[]> => {
      try {
        const evs = await fetchSeasonEvents(season.year, true, true)
        return evs || []
      } catch (err) {
        console.error('fetchAllEvents error', err)
        return []
      }
    }

    const onUpdate = (events: EventGQL[]) => {
      if (events.length > 0) {
        setEvents(events)
      }
      setIsEventsLoading(false)
    }

    try {
      await fetchAndCache<EventGQL[]>(
        `${STORAGE_NAMES.seasonEvents}_${season.year}_${season.seasonType}`,
        fetcher,
        onUpdate
      )
    } catch (err) {
      console.error('fetchAllEvents fetchAndCache error', err)
      setIsEventsLoading(false)
    }
  }

  const runCheckForLiveGameInterval = (seasonEvents: EventGQL[]) => {
    if (gameCheckIntervalRef.current === null) {
      //Starting interval to go every 3 seconds
      gameCheckIntervalRef.current = setInterval(() => {
        if (seasonEvents.length < 1) return clearInterval(liveGameIntervalRef.current)
        const liveEv = seasonEvents.find((ev) => isLiveGame(ev))
        if (liveEv) {
          startLiveGameInterval(liveEv)
        }
      }, 3000)
    } else {
      console.log('Game check interval running')
    }
  }

  const startLiveGameInterval = async (liveEv: EventGQL) => {
    if (!liveGameIntervalRef.current) {
      console.log('Starting live game interval')

      liveGameIntervalRef.current = setInterval(async () => {
        //Starting interval to go every 20 seconds
        try {
          const evInTimer = await updateInProgressGame(liveEv?.slugname)
          if (evInTimer) updateEventInState(evInTimer)

          if (!evInTimer || !isLiveGame(evInTimer)) {
            if (liveGameIntervalRef.current !== null) {
              clearInterval(liveGameIntervalRef.current)
              liveGameIntervalRef.current = null
            }
          }
        } catch (err) {
          console.log('Error getting in progress game', err)
          clearInterval(liveGameIntervalRef.current!)
        }
      }, 20000)
    }
  }

  const updateEventInState = (evToUpdate: EventGQL) => {
    const updateData = mapLiveEventForSeasonEventsUpdate(evToUpdate)
    setEvents((prev) => {
      return prev.map((ev) =>
        ev.slugname === evToUpdate.slugname ? { ...ev, ...updateData } : ev
      )
    })
  }

  // LEADERS FUNCTIONS //

  const filterLeadersByStat = useCallback((stat: PlayerSortKeys) => {
    return leagueLeaders.filter((ldr) => ldr.statType === stat)
  }, [leagueLeaders])

  const getTopPlayersByStat = (
    stat: PlayerSortKeys,
    seasonSegment: string,
    limit: number,
    teamId?: string,
    league?: string
  ) => {
    let playerList: PlayerData[] = teamId
      ? players.filter((pl) => pl.team?.officialId === teamId)
      : applyLeaderBoundaries(players, stat, seasonSelect.seasonType)
    if (playerList.length < 1) return []

    let sorted = sortPlayerFunc(playerList, stat, seasonSegment, -1)
    if (league) {
      return sorted.filter((p) => p.league === league)
    }
    if (limit) {
      sorted = sorted.slice(0, limit)
    }
    return sorted
  }

  const extractPlayerTeam = (players: PlayerData[]) => {
    if (!players || players.length < 1) return []
    return players.map((plr) => {
      const team = getLeaderTeam(seasonSelect.year, plr)
      return { ...plr, team: team ?? undefined }
    })
  }

  // TEAM FUNCTIONS //

  const getTopTeamsByStat = useCallback((stat: string, limit: number = 6) => {
    let list = [...teams]
    if (list.length === 0) {
      return []
    }
    if (seasonSelect.year === 2019 || seasonSelect.year === 2020) {
      list = filterPLLTeams(list)
    }
    if (seasonSelect.seasonType === 'champseries') {
      list = filterChampSeriesTeams(list, seasonSelect.year)
    }
    const sortedList = sortTeamsFunc(list, stat, seasonSelect.seasonType, -1).slice(
      0,
      limit
    )
    return sortedList
  }, [teams, seasonSelect.year, seasonSelect.seasonType])

  const getTopPlayersByTeamAndStat = useCallback((
    teamId: string,
    stat: PlayerSortKeys,
    limit: number = 6
  ) => {
    let ldrs = getTopPlayersByStat(stat, seasonSelect.seasonType, limit, teamId)
    let leadersWithBoundaries = applyLeaderBoundaries(
      ldrs,
      stat,
      seasonSelect.seasonType
    )
    return leadersWithBoundaries
  }, [players, seasonSelect.seasonType])

  const clearIntervals = () => {
    if (gameCheckIntervalRef.current !== null) {
      clearInterval(gameCheckIntervalRef.current)
      gameCheckIntervalRef.current = null
    }
    if (liveGameIntervalRef.current !== null) {
      clearInterval(liveGameIntervalRef.current)
      liveGameIntervalRef.current = null
    }
  }

  const contextValue: StatsContextType = useMemo(
    () => ({
      isPlayersLoading,
      isTeamsLoading,
      isEventsLoading,
      isLeagueLeadersLoading,
      players,
      postPlayers,
      csPlayers,
      teams,
      events,
      leagueLeaders,
      yearObj,
      getTopTeamsByStat,
      getTopPlayersByTeamAndStat,
      fetchLeaders,
      filterLeadersByStat,
    }),
    [
      isPlayersLoading,
      isTeamsLoading,
      isEventsLoading,
      isLeagueLeadersLoading,
      players,
      postPlayers,
      csPlayers,
      teams,
      events,
      leagueLeaders,
      yearObj,
      getTopTeamsByStat,
      getTopPlayersByTeamAndStat,
      filterLeadersByStat,
    ]
  )

  return <StatsContext.Provider value={contextValue}>{children}</StatsContext.Provider>
}
